import dayjs from "dayjs";
import { Calendar, Clock, MapPin, Users } from "lucide-react";
import type { MouseEvent } from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { EVENT_LOCATION_TYPE_ONLINE } from "@/constants";
import { useEventLinkPreviewContext } from "@/contexts/EventLinkPreviewContext";
import type { EventApiResponse } from "@/lib/humhub-api/types";

interface EventLinkPreviewProps {
  url: string;
  event: EventApiResponse;
  image?: string;
  currentStudyGroupId?: string;
  onEventClick?: (eventData: EventApiResponse) => void;
}

export const EventLinkPreview = ({
  url,
  event,
  image,
  currentStudyGroupId,
  onEventClick,
}: EventLinkPreviewProps) => {
  const { t } = useTranslation();
  const [hasImageError, setHasImageError] = useState(false);
  const startDate = dayjs(event.start_datetime);
  const {
    onEventClick: contextOnEventClick,
    currentStudyGroupId: contextStudyGroupId,
    showOriginalContent,
  } = useEventLinkPreviewContext();
  const effectiveOnEventClick = onEventClick ?? contextOnEventClick;
  const effectiveCurrentStudyGroupId =
    contextStudyGroupId ?? currentStudyGroupId;
  const displayTitle =
    showOriginalContent && event.title_original
      ? event.title_original
      : event.title;

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const isInCurrentStudyGroup =
        effectiveCurrentStudyGroupId &&
        event.container?.id &&
        String(event.container.id) === String(effectiveCurrentStudyGroupId);

      if (isInCurrentStudyGroup && effectiveOnEventClick) {
        e.preventDefault();
        effectiveOnEventClick(event);
      }
      // それ以外の場合は通常のリンクとして動作（target="_blank"）
    },
    [effectiveCurrentStudyGroupId, event, effectiveOnEventClick],
  );

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="block max-w-md cursor-pointer overflow-hidden rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-white transition-all duration-200 hover:border-blue-300 hover:shadow-lg"
    >
      <div className="p-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="shrink-0">
            <Calendar className="size-5 text-blue-600" />
          </div>
          <h3 className="line-clamp-2 text-lg font-semibold leading-tight text-blue-600 transition-colors hover:text-blue-700">
            {displayTitle}
          </h3>
        </div>

        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Users className="size-4 shrink-0" />
            <span className="truncate">{event.container?.name}</span>
          </div>

          <div className="flex items-start gap-2 text-sm text-gray-700">
            <Clock className="mt-0.5 size-4 shrink-0" />
            <div>
              <div>{startDate.format("YYYY/MM/DD HH:mm")}</div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-700">
            <MapPin className="size-4 shrink-0" />
            <span className="truncate">
              {event.location_type === EVENT_LOCATION_TYPE_ONLINE
                ? t("common.online")
                : t("common.inPerson")}
            </span>
          </div>
        </div>

        {image && !hasImageError && (
          <div className="overflow-hidden rounded-lg bg-gray-100">
            <img
              src={image}
              alt={displayTitle || "Event image"}
              className="max-h-60 w-full object-contain py-2"
              onError={() => setHasImageError(true)}
            />
          </div>
        )}
      </div>
    </a>
  );
};
